/**
 * <TutorSamSummaryCard> — un point d'avancement de Sam avec un élève, relu par le masteur (molecule).
 *
 * Une carte de l'ancien `AgentSummaryModal` : ses chips (« Nouveau », « Besoin d'aide », « Nouvelle
 * activité générée », et l'issue de l'échange : « Terminé », « Non terminé », « Interrompu »), le titre
 * que Sam a donné (à défaut « Discussion terminée » ou « non terminée ») et l'heure, le résumé, les
 * photos que l'élève a prises (« Photos prises » : une photo touchée s'ouvre, d'où le masteur en fait
 * une activité), et l'avis du masteur (Utile / Pas utile). Sam a agi : la carte porte l'aurore.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UICorrectionFeedback } from "../../../ports/primary/ui/types/studentActivity"
import { type UITutorSamSummary } from "../../../ports/primary/ui/types/tutorLive"
import { Badge } from "../../atoms/Badge"
import { Stack } from "../../atoms/containers/Stack"
import { MediaThumbnail } from "../../atoms/MediaThumbnail"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { AuroraFill } from "../AuroraFill"
import { Card } from "../Card"
import { CorrectionFeedback } from "../CorrectionFeedback"

export type TutorSamSummaryCardProps = {
    summary: UITutorSamSummary
    onOpenPhoto: (uri: string) => void
    onFeedback: (feedback: UICorrectionFeedback) => void
    testID?: string
}

const TEXTS = {
    isNew: "Nouveau",
    needsHelp: "Besoin d'aide",
    generated: "Nouvelle activité générée",
    status: { finished: "Terminé", unfinished: "Non terminé", interrupted: "Interrompu" },
    fallback: {
        finished: "Discussion terminée",
        unfinished: "Discussion non terminée",
        interrupted: "Discussion interrompue",
    },
    photos: "Photos prises",
    photo: (index: number) => `Photo ${index + 1}`,
    feedback: { question: "Ce résumé t'est utile ?", yes: "Utile", no: "Pas utile" },
}

const PHOTO_SIZE = 64

export function TutorSamSummaryCard(props: TutorSamSummaryCardProps) {
    const { summary, onOpenPhoto, onFeedback, testID } = props
    return (
        <Card p="4" testID={testID}>
            <AuroraFill />
            <Stack gap="3">
                <View style={styles.chips}>
                    {summary.isNew === true && <Badge text={TEXTS.isNew} variant="primary" truncate={false} />}
                    {summary.needsHelp === true && (
                        <Badge text={TEXTS.needsHelp} variant="danger" tinted iconName="hand-paper" truncate={false} />
                    )}
                    {summary.generatedActivity === true && (
                        <Badge text={TEXTS.generated} variant="info" tinted iconName="magic" truncate={false} />
                    )}
                    <Badge
                        text={TEXTS.status[summary.status]}
                        variant={summary.status === "finished" ? "success" : "neutral"}
                        tinted
                        truncate={false}
                    />
                </View>
                <View style={styles.head}>
                    <Text weight="bold" color="text-strong" style={styles.title}>
                        {summary.title ?? TEXTS.fallback[summary.status]}
                    </Text>
                    <Text size="text-xs" color="text-muted">
                        {summary.time}
                    </Text>
                </View>
                <Text size="text-sm" color="text-primary">
                    {summary.summary}
                </Text>
                {summary.photos.length > 0 && (
                    <Stack gap="2">
                        <SectionLabel>{TEXTS.photos}</SectionLabel>
                        <View style={styles.photos}>
                            {summary.photos.map((uri, index) => (
                                <MediaThumbnail
                                    key={uri}
                                    uri={uri}
                                    size={PHOTO_SIZE}
                                    onPress={() => onOpenPhoto(uri)}
                                    accessibilityLabel={TEXTS.photo(index)}
                                />
                            ))}
                        </View>
                    </Stack>
                )}
                <CorrectionFeedback value={summary.feedback ?? null} onChange={onFeedback} texts={TEXTS.feedback} />
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    chips: { flexDirection: "row", flexWrap: "wrap", gap: theme.spacing["2"] },
    head: { flexDirection: "row", alignItems: "baseline", gap: theme.spacing["2"] },
    title: { flex: 1 },
    photos: { flexDirection: "row", flexWrap: "wrap", gap: theme.spacing["2"] },
}))
